import type { OptionValue, Step, Substep } from "@views/Upholstery/types"
import { isM3BodyRowsSkuSegment } from "./pdm-option-sku"

/**
 * When several PDM bases share an LCP, put each remainder on body-step options
 * that still lack a real M3 segment SKU (PDM-only cars).
 *
 * Only assigns when the count of empty body-step options equals the suffix count.
 */
export function applyDerivedBodySuffixesToSteps(
  steps: Step[],
  bodySuffixes: string[]
): Step[] {
  const suffixes = bodySuffixes
    .map((s) => s.replace(/\s+/g, "").trim())
    .filter(Boolean)
  if (!suffixes.length) return steps

  return steps.map((step) => {
    if (step.key !== "body") return step

    const applyToSubstep = (substep: Substep): Substep => {
      if (substep.key !== "body-step") return substep
      const opts = substep.category?.optionValues ?? []
      if (!opts.length) return substep

      const emptyIndexes = opts
        .map((opt, index) => ({ opt, index }))
        .filter(({ opt }) => !isM3BodyRowsSkuSegment(opt.sku ?? ""))
        .map(({ index }) => index)

      if (emptyIndexes.length !== suffixes.length) return substep

      const optionValues: OptionValue[] = opts.map((opt, index) => {
        const emptyAt = emptyIndexes.indexOf(index)
        if (emptyAt < 0) return opt
        return { ...opt, sku: suffixes[emptyAt] ?? opt.sku }
      })

      return {
        ...substep,
        category: {
          ...substep.category,
          optionValues,
        },
      }
    }

    return {
      ...step,
      substeps: (step.substeps ?? []).map(applyToSubstep),
    }
  })
}
